/**
 * تاریخِ سیگنالی که از روی یک رخداد ساخته می‌شود:
 *
 *  - رخدادِ **تک‌تاریخ**: فرم با همان تاریخ (و ساعت) باز می‌شود — کاربر از روی
 *    یک چیزِ مشخص دارد برنامه می‌سازد، «کِی»اش را همان لحظه می‌دانیم.
 *  - رخدادِ **چندتاریخ** (چند اجرا، بازه، یا الگوی تکرار): کاربر خودش انتخاب
 *    می‌کند؛ اگر انتخابش با هیچ‌کدام از تاریخ‌های رخداد نخواند، هشدار می‌گیرد
 *    ولی جلوی فرستادن گرفته نمی‌شود — شاید قصدش «شبِ بعد از کنسرت» باشد.
 *
 * منطقِ «این روز، روزِ اجرای این رخداد هست؟» از discoverWhen می‌آید
 * (matchesDay/isRecurring) — همان قاعده‌ای که فیلترِ «کِی؟»ِ کشف استفاده
 * می‌کند، تا دو جای اپ دو جور جواب ندهند.
 */
import { dayOf } from './dateChoice.ts';
import { isRecurring, matchesDay, type Timeable } from './discoverWhen.ts';

/** «کِی»ِ یک اجرا — همان دو فیلدِ EventOccurrence که این‌جا لازم است. */
export interface OccurrenceWhen { startsAt?: string | null; endsAt?: string | null }

const DAY_MS = 24 * 60 * 60 * 1000;

const nextDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

/**
 * بازه‌های صریحِ روزها: ردیفِ خودِ رخداد + اجراها، هر کدام [روزِ اول..روزِ آخر]
 * به نیمه‌شبِ محلی. پایانِ عقب‌تر از شروع (داده‌ی خراب) همان تک‌روز حساب می‌شود.
 */
function daySpans(e: Timeable, occurrences: OccurrenceWhen[]): { from: Date; to: Date }[] {
  const out: { from: Date; to: Date }[] = [];
  for (const o of [e, ...occurrences]) {
    if (!o.startsAt) continue;
    const from = dayOf(new Date(o.startsAt));
    const to = o.endsAt ? dayOf(new Date(o.endsAt)) : from;
    out.push({ from, to: to.getTime() >= from.getTime() ? to : from });
  }
  return out;
}

/**
 * تاریخِ **یک‌تکه**ی رخداد: یک روز، یا یک بازه‌ی پشتِ‌سرِهم. `null` یعنی چند
 * تاریخِ جدا (اجراها) یا الگوی تکرار — آن‌جا انتخاب با خودِ کاربر است و
 * پیش‌پر کردنش یعنی حدس زدن به‌جای او.
 *
 * `end` خالیْ یعنی همان یک روز. پایانِ هم‌روزِ شروع (۱۹ تا ۲۱ همان شب) بازه
 * حساب نمی‌شود — همان قاعده‌ی `isSameDay` که برچسب‌ها هم به کار می‌برند، تا
 * فرم و کارت و صفحه‌ی جزئیات یک چیز بگویند.
 */
export function eventDaySpan(
  e: Timeable, occurrences: OccurrenceWhen[],
): { start: Date; end: Date | null } | null {
  if (isRecurring(e)) return null;
  const spans = daySpans(e, occurrences);
  if (spans.length !== 1) return null;
  const { from, to } = spans[0];
  return { start: from, end: to.getTime() === from.getTime() ? null : to };
}

/**
 * ساعتِ شروعِ رخداد برای پیش‌پرِ فرم. نیمه‌شبِ دقیق یعنی «منبع فقط تاریخ داده»
 * — همان قاعده‌ی itemFields — پس null، تا سیگنال «فقط روز» بماند.
 */
export function eventStartClock(startsAt: string): { h: number; m: number } | null {
  const d = new Date(startsAt);
  return d.getHours() === 0 && d.getMinutes() === 0
    ? null : { h: d.getHours(), m: d.getMinutes() };
}

/**
 * بازه‌ی روزهای یک گزینه‌ی تقریبیِ «کِی؟» («این آخر هفته») — تا انتخابِ
 * تقریبی هم مثلِ تاریخِ دقیق با تاریخ‌های رخداد سنجیده شود؛ «رخدادِ
 * سه‌شنبه + انتخابِ آخرِ هفته» بی‌هشدار رد می‌شد.
 *
 * `null` یعنی این نشانه روزِ قابلِ‌سنجش ندارد (متنِ آزاد، نشانه‌ی ناشناخته)
 * — و سنجش نکردن یعنی هشدار ندادن، همان سمتِ سخاوتمندِ همیشگی.
 *
 * روزِ هفته‌ی `@around` با قرارِ دیتابیس است (۰=شنبه … ۶=جمعه — همان که
 * days()ِ i18n و approxLabel می‌خوانند)؛ جاوااسکریپت از یکشنبه می‌شمارد.
 */
export function approxDays(
  token: string, now: Date = new Date(),
): { start: Date; end: Date | null } | null {
  const today = dayOf(now);
  const plus = (n: number): Date =>
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + n);
  const dow = now.getDay(); // ۰=یکشنبه … ۶=شنبه، به قرارِ جاوااسکریپت

  switch (token) {
    case '@tonight':
      return { start: today, end: null };
    case '@weekend': {
      // شنبه و یکشنبه؛ اگر وسطشیم از امروز — همان قاعده‌ی discoverWhen.
      if (dow === 6) return { start: today, end: plus(1) };
      if (dow === 0) return { start: today, end: null };
      return { start: plus(6 - dow), end: plus(6 - dow + 1) };
    }
    case '@friday':
      return { start: plus((5 - dow + 7) % 7), end: null };
    case '@next_week': {
      // هفت روز از دوشنبه‌ی بعدی؛ «هفته‌ی بعد» هیچ‌وقت امروز نیست.
      const untilMonday = ((1 - dow + 7) % 7) || 7;
      return { start: plus(untilMonday), end: plus(untilMonday + 6) };
    }
    default: {
      const m = token.match(/^@around:(\d+):(\d+)$/);
      if (!m || Number(m[1]) > 6) return null;
      const js = (Number(m[1]) + 6) % 7;
      return { start: plus((js - dow + 7) % 7), end: null };
    }
  }
}

/**
 * انتخابِ کاربر (یک روز، یا بازه‌ی start..end) با تاریخ‌های رخداد می‌خواند؟
 *
 * برای بازه کافی است **یک** روزش بخواند: «از پنجشنبه تا شنبه» برای رخدادِ
 * جمعه‌ها، انتخابِ غلطی نیست.
 *
 * دو جای عمداً سخاوتمند — هشدارِ اشتباه بدتر از هشدارِ ندادن است:
 *  - رخدادِ بی‌تاریخ و بی‌الگو: چیزی برای سنجیدن نیست → می‌خواند.
 *  - بازه‌ی بلندتر از دو ماه: پیمایشِ روزبه‌روز را همان اول رها می‌کنیم → می‌خواند.
 */
export function choiceOnEvent(
  e: Timeable, occurrences: OccurrenceWhen[], start: Date, end?: Date | null,
): boolean {
  const spans = daySpans(e, occurrences);
  if (!spans.length && !isRecurring(e)) return true;
  const from = dayOf(start);
  const to = end ? dayOf(end) : from;
  if (to.getTime() - from.getTime() > 62 * DAY_MS) return true;
  for (let d = from; d.getTime() <= to.getTime(); d = nextDay(d)) {
    if (spans.some((s) => d.getTime() >= s.from.getTime() && d.getTime() <= s.to.getTime())) {
      return true;
    }
    if (matchesDay(e, d)) return true;
  }
  return false;
}
